import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Calculator, CloudUpload, Filter, Eye, PencilLine, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatMXN } from '@/components/ContractConceptsList';
import { PdfPreviewModal } from '@/components/pdf/PdfPreviewModal';
import { PricingCell } from '@/components/presupuesto/PricingCell';
import { offlineDb } from '@/offline/db';
import { syncManager } from '@/offline/syncManager';
import { generatePresupuestoPdf, IVA_RATE } from '@/lib/presupuestoPdf';
import { cdTotal, cdTotalIsAuto, clientPrices, margenPct, numOrNull, utilidadConMaterial, utilidadMo } from '@/lib/conceptPricing';
import { groupConcepts, hasAreas, SIN_AREA, SIN_PARTIDA } from '@/components/ConceptGroupPicker';
import { MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cantidad neta por concepto: Σ suma − Σ deducción (figuras del servidor + figuras locales pendientes)
const netByConcept = (figures) => {
  const map = {};
  figures.forEach((f) => {
    const q = Number(f.cantidad) || 0;
    map[f.concept_id] = (map[f.concept_id] || 0) + (f.tipo === 'deduccion' ? -q : q);
  });
  return map;
};

const formatQty = (q) => (Math.abs(q) < 0.01 && q !== 0 ? q.toFixed(4) : q.toFixed(2));
const formatDiff = (d) => `${d > 0 ? '+' : d < 0 ? '−' : ''}${formatQty(Math.abs(d))}`;
const formatPct = (d, base) => (base > 0 ? `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs((d / base) * 100).toFixed(1)}%` : '');
const money = (v) => (v === null || v === undefined ? '—' : formatMXN(v));

// Agrupación visual por color: misma familia azul (#0066FF), distinto tono por grupo
const G = {
  cd: { head: 'bg-[#00234F] text-[#8FBBFF]', cell: 'bg-[#0066FF]/[0.06]' },
  pu: { head: 'bg-[#0047B3] text-white', cell: 'bg-[#0066FF]/[0.14]' },
  ut: { head: 'bg-[#0066FF] text-white', cell: 'bg-[#0066FF]/[0.24]' },
};

/* Fuente de la cantidad: Estimado (Cantidad Total manual, sin figuras) vs Levantado (figuras en campo) */
const FuenteTag = ({ fuente, id }) => {
  if (fuente === 'levantado') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#22C55E]/15 px-2 py-0.5 text-[10px] font-semibold text-[#22C55E]" data-testid={`presupuesto-fuente-${id}`}>
        <CheckCircle2 className="w-3 h-3" /> Generado
      </span>
    );
  }
  if (fuente === 'estimado') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-semibold text-[#F59E0B]" data-testid={`presupuesto-fuente-${id}`}>
        <PencilLine className="w-3 h-3" /> Referencia
      </span>
    );
  }
  return <span className="text-[10px] text-[#5A7A9A]" data-testid={`presupuesto-fuente-${id}`}>—</span>;
};

const UtilidadCell = ({ utilidad, pu, testId }) => {
  if (utilidad === null) return <span className="text-[#5A7A9A] font-mono" data-testid={testId}>—</span>;
  const pct = margenPct(utilidad, pu);
  return (
    <span className={`font-mono font-semibold ${utilidad < 0 ? 'text-[#FF4D4D]' : 'text-[#22C55E]'}`} data-testid={testId}>
      {formatMXN(utilidad)}
      {pct !== null && <span className="ml-1 text-[10px] opacity-75">({pct.toFixed(1)}%)</span>}
    </span>
  );
};

/* Actualización de Presupuesto: cantidad × PU, sin referencia a facturación */
export const PresupuestoPage = () => {
  const navigate = useNavigate();
  const { selectedProject, concepts, refreshConcepts } = useApp();
  const { user } = useAuth();
  const [serverFigures, setServerFigures] = useState([]);
  const [localFigures, setLocalFigures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromCache, setFromCache] = useState(false);
  const [onlyConCantidad, setOnlyConCantidad] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // { blob, filename }
  const [pricingOverrides, setPricingOverrides] = useState({}); // { conceptId: { campo: valor } }
  const [collapsedAreas, setCollapsedAreas] = useState({}); // áreas colapsadas en la tabla

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/figures`, { params: { project_id: selectedProject.id, view: 'presupuesto' }, timeout: 8000 });
      setServerFigures(res.data || []);
      setFromCache(!navigator.onLine);
    } catch (err) {
      // Sin red y sin caché del SW: se trabaja solo con datos locales
      setFromCache(true);
    }
    try {
      const all = await offlineDb.getAllFigures();
      // Presupuesto: solo figuras de presupuesto o legacy (destino != 'avance')
      setLocalFigures(all.filter((r) => r.project_id === selectedProject.id && (r.payload?.destino ?? null) !== 'avance').map((r) => r.payload));
    } catch (err) {
      setLocalFigures([]);
    }
    setLoading(false);
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      navigate('/contratos');
      return;
    }
    loadData();
  }, [selectedProject, navigate, loadData]);

  // Refrescar cuando cambian pendientes locales o terminan de sincronizar conceptos
  useEffect(() => {
    const onChange = () => loadData();
    const onConcepts = () => { refreshConcepts(); loadData(); };
    syncManager.addEventListener('change', onChange);
    syncManager.addEventListener('concepts-changed', onConcepts);
    return () => {
      syncManager.removeEventListener('change', onChange);
      syncManager.removeEventListener('concepts-changed', onConcepts);
    };
  }, [loadData, refreshConcepts, selectedProject]);

  // Guardar un campo de precio del concepto (requiere conexión: vive en el catálogo)
  const savePricing = async (concept, field, value) => {
    if (concept.local) {
      toast.error('Sincroniza el concepto antes de capturar precios');
      return;
    }
    if (!navigator.onLine) {
      toast.error('Se necesita conexión para guardar precios');
      return;
    }
    try {
      await axios.put(`${API}/concepts/${concept.id}`, { [field]: value }, { timeout: 10000 });
      setPricingOverrides((prev) => ({ ...prev, [concept.id]: { ...(prev[concept.id] || {}), [field]: value } }));
      refreshConcepts();
    } catch (err) {
      console.error('Error guardando precio:', err);
      toast.error('No se pudo guardar el precio');
    }
  };

  // Regla: con figuras → cantidad levantada (reemplaza al estimado); sin figuras → Cantidad Total manual como estimado provisional
  const rows = useMemo(() => {
    const serverIds = new Set(serverFigures.map((f) => f.id));
    const merged = [...serverFigures, ...localFigures.filter((f) => !serverIds.has(f.id))];
    const net = netByConcept(merged);
    const rank = { levantado: 0, estimado: 1 };
    return concepts
      .map((raw) => {
        const c = { ...raw, ...(pricingOverrides[raw.id] || {}) };
        const hasFiguras = Object.prototype.hasOwnProperty.call(net, c.id);
        const estimado = Number(c.contratado) || 0;
        const levantado = hasFiguras ? net[c.id] : 0;
        const fuente = hasFiguras ? 'levantado' : estimado > 0 ? 'estimado' : null;
        const cantidad = hasFiguras ? levantado : estimado;
        const diferencia = hasFiguras && estimado > 0 ? levantado - estimado : null;
        const pu = Number(c.precio_unitario) || 0;
        const { puMo, puMat } = clientPrices(c);
        const cd = cdTotal(c);
        return {
          ...c,
          cantidad,
          estimado,
          levantado,
          fuente,
          diferencia,
          pu,
          importe: cantidad * pu,
          cdMo: numOrNull(c.cd_mo),
          cdMat: numOrNull(c.cd_materiales),
          cdTotalManual: numOrNull(c.cd_total_manual),
          cd,
          cdAuto: cdTotalIsAuto(c),
          puMo,
          puMat,
          importeMo: puMo === null ? null : cantidad * puMo,
          importeMat: puMat === null ? null : cantidad * puMat,
          utilMo: utilidadMo(c),
          utilMat: utilidadConMaterial(c),
        };
      })
      .sort((a, b) => (rank[a.fuente] ?? 2) - (rank[b.fuente] ?? 2) || (a.order || 0) - (b.order || 0));
  }, [concepts, serverFigures, localFigures, pricingOverrides]);

  const conCantidad = rows.filter((r) => r.fuente);
  const visibleRows = onlyConCantidad ? conCantidad : rows;
  const subtotalLevantado = rows.filter((r) => r.fuente === 'levantado').reduce((acc, r) => acc + r.importe, 0);
  const subtotalEstimado = rows.filter((r) => r.fuente === 'estimado').reduce((acc, r) => acc + r.importe, 0);
  const total = subtotalLevantado + subtotalEstimado;
  const iva = total * IVA_RATE;
  const totalConIva = total + iva;
  const conceptosLevantados = rows.filter((r) => r.fuente === 'levantado').length;
  const conceptosEstimados = rows.filter((r) => r.fuente === 'estimado').length;
  const sinPU = conCantidad.filter((r) => r.pu === 0 && r.puMo === null && r.puMat === null).length;

  // Resúmenes internos por grupo (sobre los conceptos con cantidad)
  const sumCdTotal = conCantidad.reduce((acc, r) => acc + (r.cd === null ? 0 : r.cd * r.cantidad), 0);
  const sumPuMo = conCantidad.reduce((acc, r) => acc + (r.importeMo || 0), 0);
  const sumPuMat = conCantidad.reduce((acc, r) => acc + (r.importeMat || 0), 0);
  const sumUtilMo = conCantidad.reduce((acc, r) => acc + (r.utilMo === null ? 0 : r.utilMo * r.cantidad), 0);
  const sumUtilMat = conCantidad.reduce((acc, r) => acc + (r.utilMat === null ? 0 : r.utilMat * r.cantidad), 0);

  // PDF generado en el navegador con los datos ya cargados (funciona sin conexión) → vista previa
  const handlePreviewPdf = async (priceMode = 'both') => {
    if (conCantidad.length === 0) {
      toast.error('Aún no hay conceptos con cantidad para exportar');
      return;
    }
    setExporting(true);
    try {
      const { blob, filename } = await generatePresupuestoPdf({
        projectName: selectedProject.name,
        numeroContrato: selectedProject.numero_contrato,
        ubicacion: selectedProject.ubicacion,
        rows: conCantidad,
        generatedBy: user?.name,
        priceMode,
      });
      setPdfPreview({ blob, filename });
    } catch (err) {
      console.error('Error generando PDF de presupuesto:', err);
      toast.error('No se pudo generar el PDF');
    } finally {
      setExporting(false);
    }
  };

  if (!selectedProject) return null;

  const groupedView = hasAreas(visibleRows);

  const renderPresupuestoRow = (r) => (
    <tr key={r.id} className={!r.fuente ? 'opacity-50' : ''} data-testid={`presupuesto-row-${r.id}`} data-fuente={r.fuente || 'ninguna'}>
      <td className="px-3 py-3 font-mono text-[#00CFFF] whitespace-nowrap">
        {r.clave}
        {r.local && <span className="ml-1 rounded-full bg-[#FF6B00]/15 px-1.5 text-[9px] font-semibold text-[#FF6B00]">local</span>}
      </td>
      <td className="px-3 py-3 text-[#E2EAF4] max-w-md"><span className="line-clamp-2">{r.descripcion}</span></td>
      <td className={`px-3 py-3 text-right font-mono font-semibold ${r.fuente === 'estimado' ? 'text-[#F59E0B]' : 'text-[#00CFFF]'}`} data-testid={`presupuesto-cantidad-${r.id}`}>{formatQty(r.cantidad)}</td>
      <td className="px-3 py-3 text-center"><FuenteTag fuente={r.fuente} id={r.id} /></td>
      <td className="px-3 py-3 text-right font-mono whitespace-nowrap" data-testid={`presupuesto-diferencia-${r.id}`}>
        {r.diferencia === null ? (
          <span className="text-[#5A7A9A]">—</span>
        ) : (
          <span className={r.diferencia > 0 ? 'text-[#FF6B00]' : r.diferencia < 0 ? 'text-[#00CFFF]' : 'text-[#5A7A9A]'}>
            {formatDiff(r.diferencia)}
            <span className="ml-1 text-[10px] opacity-75">({formatPct(r.diferencia, r.estimado)})</span>
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-center text-[#5A7A9A]">{r.unidad}</td>

      {/* Grupo Costo Directo (interno) */}
      <td className={`px-2 py-3 whitespace-nowrap ${G.cd.cell}`}>
        <PricingCell value={r.cdMo} onSave={(v) => savePricing(r, 'cd_mo', v)} testId={`presupuesto-cd-mo-${r.id}`} accentClass="text-[#BFD8FF]" />
      </td>
      <td className={`px-2 py-3 whitespace-nowrap ${G.cd.cell}`}>
        <PricingCell value={r.cdMat} onSave={(v) => savePricing(r, 'cd_materiales', v)} testId={`presupuesto-cd-materiales-${r.id}`} accentClass="text-[#BFD8FF]" />
      </td>
      <td className={`px-2 py-3 whitespace-nowrap ${G.cd.cell}`}>
        {r.cdAuto ? (
          <span className="block text-right font-mono font-semibold text-[#8FBBFF] px-1" title="Calculado: CD M.O. + CD Materiales" data-testid={`presupuesto-cd-total-${r.id}`}>
            {money(r.cd)}
          </span>
        ) : (
          <PricingCell value={r.cdTotalManual} onSave={(v) => savePricing(r, 'cd_total_manual', v)} testId={`presupuesto-cd-total-${r.id}`} accentClass="text-[#8FBBFF]" />
        )}
      </td>

      {/* Grupo PU Cliente (sí va al PDF) */}
      <td className={`px-2 py-3 whitespace-nowrap ${G.pu.cell}`}>
        <PricingCell value={numOrNull(r.pu_mo)} onSave={(v) => savePricing(r, 'pu_mo', v)} testId={`presupuesto-pu-mo-${r.id}`} accentClass="text-white" />
      </td>
      <td className={`px-3 py-3 text-right font-mono whitespace-nowrap ${G.pu.cell} ${r.importeMo === null ? 'text-[#5A7A9A]' : 'text-white'}`} data-testid={`presupuesto-importe-mo-${r.id}`}>
        {money(r.importeMo)}
      </td>
      <td className={`px-2 py-3 whitespace-nowrap ${G.pu.cell}`}>
        <PricingCell value={numOrNull(r.pu_con_material)} onSave={(v) => savePricing(r, 'pu_con_material', v)} testId={`presupuesto-pu-material-${r.id}`} accentClass="text-white" />
      </td>
      <td className={`px-3 py-3 text-right font-mono whitespace-nowrap ${G.pu.cell} ${r.importeMat === null ? 'text-[#5A7A9A]' : 'text-white'}`} data-testid={`presupuesto-importe-material-${r.id}`}>
        {money(r.importeMat)}
      </td>

      {/* Grupo Utilidad (interno) */}
      <td className={`px-3 py-3 text-right whitespace-nowrap ${G.ut.cell}`}>
        <UtilidadCell utilidad={r.utilMo} pu={r.puMo} testId={`presupuesto-utilidad-mo-${r.id}`} />
      </td>
      <td className={`px-3 py-3 text-right whitespace-nowrap ${G.ut.cell}`}>
        <UtilidadCell utilidad={r.utilMat} pu={r.puMat} testId={`presupuesto-utilidad-material-${r.id}`} />
      </td>

      <td className="px-3 py-3 text-right font-mono text-[#5A7A9A] whitespace-nowrap">{formatMXN(r.pu)}</td>
      <td className="px-3 py-3 text-right font-mono font-semibold text-[#22C55E] whitespace-nowrap" data-testid={`presupuesto-importe-${r.id}`}>{formatMXN(r.importe)}</td>
      <td className="px-3 py-3 text-right font-mono text-[#5A7A9A] whitespace-nowrap" data-testid={`presupuesto-iva-${r.id}`}>{formatMXN(r.importe * IVA_RATE)}</td>
      <td className="px-3 py-3 text-right font-mono font-semibold text-[#E2EAF4] whitespace-nowrap" data-testid={`presupuesto-total-iva-${r.id}`}>{formatMXN(r.importe * (1 + IVA_RATE))}</td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="presupuesto-page">
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contrato')} className="text-[#E2EAF4] hover:bg-[#1E3550]" data-testid="back-to-contrato">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-[#00CFFF]">ACTUALIZACIÓN DE PRESUPUESTO</h1>
            <p className="text-[#5A7A9A] text-sm truncate">{selectedProject.name}</p>
            <p className="text-xs text-[#5A7A9A] mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="inline-flex items-center gap-1 text-[#22C55E]" data-testid="presupuesto-count-levantados">
                <CheckCircle2 className="w-3 h-3" /> {conceptosLevantados} generado{conceptosLevantados !== 1 ? 's' : ''}
              </span>
              <span className="inline-flex items-center gap-1 text-[#F59E0B]" data-testid="presupuesto-count-estimados">
                <PencilLine className="w-3 h-3" /> {conceptosEstimados} referencia{conceptosEstimados !== 1 ? 's' : ''}
              </span>
              {localFigures.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[#FF6B00]" data-testid="presupuesto-local-badge">
                  <CloudUpload className="w-3 h-3" /> incluye {localFigures.length} figura{localFigures.length !== 1 ? 's' : ''} sin sincronizar
                </span>
              )}
              {fromCache && <span className="text-[#FF6B00]" data-testid="presupuesto-offline-badge">· datos guardados en el dispositivo</span>}
            </p>
          </div>
          <div className="text-right" data-testid="presupuesto-total-box">
            <p className="text-[10px] uppercase tracking-wide text-[#5A7A9A]">Total con IVA</p>
            <p className="font-mono font-bold text-2xl text-[#00CFFF]" data-testid="presupuesto-total">{formatMXN(totalConIva)}</p>
            <p className="text-[11px] text-[#5A7A9A] font-mono">
              Subtotal <span data-testid="presupuesto-subtotal">{formatMXN(total)}</span> · IVA {Math.round(IVA_RATE * 100)}% <span data-testid="presupuesto-iva">{formatMXN(iva)}</span>
            </p>
            <p className="text-[11px] font-mono">
              <span className="text-[#22C55E]">Generado <span data-testid="presupuesto-subtotal-levantado">{formatMXN(subtotalLevantado)}</span></span>
              <span className="text-[#5A7A9A]"> · </span>
              <span className="text-[#F59E0B]">Referencia <span data-testid="presupuesto-subtotal-estimado">{formatMXN(subtotalEstimado)}</span></span>
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={exporting || conCantidad.length === 0}
                className="bg-[#FF6B00] hover:bg-[#FF6B00]/80 text-white font-semibold"
                data-testid="btn-preview-presupuesto-pdf"
              >
                {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Vista Previa
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0D1B2E] border-[#1E3550] text-[#E2EAF4]">
              <DropdownMenuLabel className="text-[#5A7A9A] text-xs">Columnas de precio en el PDF</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#1E3550]" />
              <DropdownMenuItem className="focus:bg-[#1E3550] focus:text-white cursor-pointer" onClick={() => handlePreviewPdf('mo')} data-testid="pdf-mode-mo">
                Solo M.O.
              </DropdownMenuItem>
              <DropdownMenuItem className="focus:bg-[#1E3550] focus:text-white cursor-pointer" onClick={() => handlePreviewPdf('material')} data-testid="pdf-mode-material">
                Con Material
              </DropdownMenuItem>
              <DropdownMenuItem className="focus:bg-[#1E3550] focus:text-white cursor-pointer" onClick={() => handlePreviewPdf('both')} data-testid="pdf-mode-both">
                Ambas
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={loadData} disabled={loading} className="text-[#E2EAF4] hover:bg-[#1E3550]" data-testid="btn-refresh-presupuesto">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Resumen interno por grupo (no se imprime en el PDF del cliente) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="presupuesto-resumen-grupos">
          <div className={`rounded-xl border border-[#0066FF]/20 p-4 ${G.cd.cell}`} data-testid="resumen-cd-total">
            <p className="text-[10px] uppercase tracking-wide text-[#8FBBFF]">Costo Directo Total</p>
            <p className="font-mono font-bold text-lg text-[#E2EAF4]">{formatMXN(sumCdTotal)}</p>
            <p className="text-[10px] text-[#5A7A9A]">interno · cantidad × CD</p>
          </div>
          <div className={`rounded-xl border border-[#0066FF]/30 p-4 ${G.pu.cell}`} data-testid="resumen-pu-mo">
            <p className="text-[10px] uppercase tracking-wide text-[#BFD8FF]">Importe Solo M.O.</p>
            <p className="font-mono font-bold text-lg text-white">{formatMXN(sumPuMo)}</p>
            <p className="text-[10px] text-[#5A7A9A]">cliente pone material</p>
          </div>
          <div className={`rounded-xl border border-[#0066FF]/30 p-4 ${G.pu.cell}`} data-testid="resumen-pu-material">
            <p className="text-[10px] uppercase tracking-wide text-[#BFD8FF]">Importe Con Material</p>
            <p className="font-mono font-bold text-lg text-white">{formatMXN(sumPuMat)}</p>
            <p className="text-[10px] text-[#5A7A9A]">llave en mano</p>
          </div>
          <div className={`rounded-xl border border-[#0066FF]/40 p-4 ${G.ut.cell}`} data-testid="resumen-utilidad">
            <p className="text-[10px] uppercase tracking-wide text-[#DCEAFF]">Utilidad</p>
            <p className="font-mono font-bold text-sm text-[#22C55E]">
              Solo M.O. {formatMXN(sumUtilMo)}
            </p>
            <p className="font-mono font-bold text-sm text-[#22C55E]">
              Con material {formatMXN(sumUtilMat)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setOnlyConCantidad((v) => !v)}
            className="inline-flex items-center gap-2 text-sm text-[#5A7A9A] hover:text-[#E2EAF4]"
            data-testid="toggle-only-levantados"
          >
            <Filter className="w-4 h-4" />
            {onlyConCantidad ? 'Mostrar todos los conceptos' : 'Mostrar solo conceptos con cantidad'}
          </button>
          {sinPU > 0 && (
            <span className="text-xs text-[#FF6B00]" data-testid="presupuesto-sin-pu">
              {sinPU} concepto{sinPU !== 1 ? 's' : ''} con cantidad sin PU (importe $0.00)
            </span>
          )}
        </div>

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-[#5A7A9A]"><RefreshCw className="w-6 h-6 animate-spin mr-2" /> Calculando...</div>
        ) : visibleRows.length === 0 ? (
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-10 text-center text-[#5A7A9A]" data-testid="presupuesto-empty">
            <Calculator className="w-10 h-10 mx-auto mb-3 text-[#1E3550]" />
            Aún no hay conceptos con cantidad en este contrato. Captura Cantidad Total en el catálogo (referencia) o figuras en CAPTURAR (generado) y el importe se calculará aquí automáticamente.
          </div>
        ) : (
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#1E3550]">
                <tr>
                  <th className="bg-[#0A1422]" colSpan={6} />
                  <th className={`px-3 py-1.5 text-center text-[10px] font-bold uppercase tracking-wide ${G.cd.head}`} colSpan={3} data-testid="group-header-cd">Costo Directo (interno)</th>
                  <th className={`px-3 py-1.5 text-center text-[10px] font-bold uppercase tracking-wide ${G.pu.head}`} colSpan={4} data-testid="group-header-pu">PU Cliente (va al PDF)</th>
                  <th className={`px-3 py-1.5 text-center text-[10px] font-bold uppercase tracking-wide ${G.ut.head}`} colSpan={2} data-testid="group-header-utilidad">Utilidad (interno)</th>
                  <th className="bg-[#0A1422]" colSpan={4} />
                </tr>
                <tr className="bg-[#0A1422]">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-[#5A7A9A] uppercase">Clave</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-[#5A7A9A] uppercase">Descripción</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#00CFFF] uppercase">Cantidad</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-[#5A7A9A] uppercase">Fuente</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase whitespace-nowrap" title="Generado − Referencia">Dif. vs Referencia</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-[#5A7A9A] uppercase">Unidad</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#8FBBFF] uppercase whitespace-nowrap ${G.cd.cell}`}>CD M.O.</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#8FBBFF] uppercase whitespace-nowrap ${G.cd.cell}`}>CD Materiales</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#8FBBFF] uppercase whitespace-nowrap ${G.cd.cell}`}>CD Total</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#BFD8FF] uppercase whitespace-nowrap ${G.pu.cell}`}>PU Solo M.O.</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#BFD8FF] uppercase whitespace-nowrap ${G.pu.cell}`}>Importe Solo M.O.</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#BFD8FF] uppercase whitespace-nowrap ${G.pu.cell}`}>PU Con Material</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#BFD8FF] uppercase whitespace-nowrap ${G.pu.cell}`}>Importe Con Material</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#DCEAFF] uppercase whitespace-nowrap ${G.ut.cell}`}>Utilidad Solo M.O.</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold text-[#DCEAFF] uppercase whitespace-nowrap ${G.ut.cell}`}>Utilidad Con Material</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">PU Base</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">Importe</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">IVA {Math.round(IVA_RATE * 100)}%</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#00CFFF] uppercase">Total con IVA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E3550]">
                {groupedView
                  ? Object.entries(groupConcepts(visibleRows)).map(([area, byPartida]) => {
                      const areaRows = Object.values(byPartida).flat();
                      const areaTotalIva = areaRows.reduce((a, r) => a + r.importe * (1 + IVA_RATE), 0);
                      const isCollapsed = !!collapsedAreas[area];
                      const partidaKeys = Object.keys(byPartida);
                      const showPartidas = partidaKeys.length > 1 || partidaKeys[0] !== SIN_PARTIDA;
                      return (
                        <React.Fragment key={area}>
                          <tr className="bg-[#0D1B2E] cursor-pointer select-none" onClick={() => setCollapsedAreas((o) => ({ ...o, [area]: !isCollapsed }))} data-testid={`presupuesto-area-row-${area}`}>
                            <td className="px-3 py-2.5 font-semibold text-[#E2EAF4]" colSpan={18}>
                              <span className="inline-flex items-center gap-1.5">
                                {isCollapsed ? <ChevronRight className="w-4 h-4 text-[#5A7A9A]" /> : <ChevronDown className="w-4 h-4 text-[#5A7A9A]" />}
                                <MapPin className="w-3.5 h-3.5 text-[#00CFFF]" />
                                {area === SIN_AREA ? <span className="text-[#5A7A9A] italic">{area}</span> : area}
                                <span className="text-[10px] font-normal text-[#5A7A9A]">{areaRows.length} concepto{areaRows.length !== 1 ? 's' : ''}</span>
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-[#00CFFF] whitespace-nowrap" data-testid={`presupuesto-area-total-${area}`}>{formatMXN(areaTotalIva)}</td>
                          </tr>
                          {!isCollapsed && partidaKeys.map((partida) => (
                            <React.Fragment key={`${area}-${partida}`}>
                              {showPartidas && (
                                <tr className="bg-[#0A1422]" data-testid={`presupuesto-partida-row-${area}-${partida}`}>
                                  <td className="px-3 py-1.5 pl-9 text-[11px] font-semibold uppercase tracking-wide text-[#A855F7]" colSpan={19}>
                                    {partida === SIN_PARTIDA ? <span className="text-[#5A7A9A] italic normal-case">{partida}</span> : partida}
                                    <span className="ml-2 text-[10px] font-normal text-[#5A7A9A] normal-case">{byPartida[partida].length}</span>
                                  </td>
                                </tr>
                              )}
                              {byPartida[partida].map(renderPresupuestoRow)}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      );
                    })
                  : visibleRows.map(renderPresupuestoRow)}
              </tbody>
              <tfoot>
                <tr className="bg-[#0A1422] border-t-2 border-[#1E3550]" data-testid="presupuesto-subtotal-levantado-row">
                  <td className="px-3 py-2 text-left text-[#22C55E]" colSpan={18}>SUBTOTAL GENERADO</td>
                  <td className="px-3 py-2 text-right font-mono text-[#22C55E] whitespace-nowrap">{formatMXN(subtotalLevantado)}</td>
                </tr>
                <tr className="bg-[#0A1422]" data-testid="presupuesto-subtotal-estimado-row">
                  <td className="px-3 py-2 text-left text-[#F59E0B]" colSpan={18}>SUBTOTAL REFERENCIA (provisional)</td>
                  <td className="px-3 py-2 text-right font-mono text-[#F59E0B] whitespace-nowrap">{formatMXN(subtotalEstimado)}</td>
                </tr>
                <tr className="bg-[#0A1422] border-t border-[#1E3550]" data-testid="presupuesto-subtotal-row">
                  <td className="px-3 py-2 text-left text-[#5A7A9A]" colSpan={18}>SUBTOTAL</td>
                  <td className="px-3 py-2 text-right font-mono text-[#E2EAF4] whitespace-nowrap">{formatMXN(total)}</td>
                </tr>
                <tr className="bg-[#0A1422]" data-testid="presupuesto-iva-row">
                  <td className="px-3 py-2 text-left text-[#5A7A9A]" colSpan={18}>IVA {Math.round(IVA_RATE * 100)}%</td>
                  <td className="px-3 py-2 text-right font-mono text-[#E2EAF4] whitespace-nowrap">{formatMXN(iva)}</td>
                </tr>
                <tr className="bg-[#FF6B00] font-bold" data-testid="presupuesto-totals-row">
                  <td className="px-3 py-3 text-left text-white" colSpan={18}>TOTAL</td>
                  <td className="px-3 py-3 text-right font-mono text-lg text-white whitespace-nowrap" data-testid="presupuesto-total-footer">{formatMXN(totalConIva)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </main>

      {pdfPreview && (
        <PdfPreviewModal
          pdfBlob={pdfPreview.blob}
          pdfFilename={pdfPreview.filename}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </div>
  );
};

export default PresupuestoPage;
